import * as React from "react";
import { useConfigProvider } from "../../../hooks/useConfigProvider";
import { useLoggers } from "../../../hooks/useLoggers";
import { useStores } from "../../../hooks/useStores";
import { Store } from "@ms/uno-stores/lib/local/stores/Store";
import { getLocalStorage } from "@ms/uno-utilities/lib/local/storage/StorageUtilities";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { ICalendarDeprecationBanner } from "./ICalendarDeprecationBanner";
/**
 * Local storage key for tracking whether user has dismissed the iCalendar deprecation banner
 */ const ICAL_DEPRECATION_BANNER_DISMISSED_KEY = "hasSeenICalDeprecationBanner";
/**
 * Container component that manages the iCalendar deprecation banner state
 * Handles flight checking, dismissal tracking, localStorage persistence, and iCalendar enabled checking
 */ export const ICalendarDeprecationBannerContainer = ({ planId })=>{
    const configProvider = useConfigProvider();
    const { traceLogger } = useLoggers();
    const [appContextStore, planStore, userStore] = useStores([
        Store.AppContext,
        Store.BasicPlan,
        Store.User
    ]);
    const [hasSeenBanner, setHasSeenBanner] = React.useState(false);
    const [isICalendarEnabled, setIsICalendarEnabled] = React.useState(false);
    // Load banner dismissal state from UNO localStorage on mount
    React.useEffect(()=>{
        try {
            const userId = appContextStore.getAppContext().userId;
            if (!userId) {
                return;
            }
            const storage = getLocalStorage(userId, traceLogger, configProvider);
            if (storage) {
                const dismissed = storage.getItem(ICAL_DEPRECATION_BANNER_DISMISSED_KEY);
                setHasSeenBanner(dismissed === "true");
            }
        } catch (error) {
            traceLogger.logTrace(0x1e11d3d4 /* tag_4e3pu */ , TraceLevel.Error, `Failed to read iCalendar banner dismissal state from localStorage: ${error}`);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleDismiss = ()=>{
        try {
            const userId = appContextStore.getAppContext().userId;
            if (!userId) {
                return;
            }
            const storage = getLocalStorage(userId, traceLogger, configProvider);
            if (storage) {
                storage.setItem(ICAL_DEPRECATION_BANNER_DISMISSED_KEY, "true");
                setHasSeenBanner(true);
            }
        } catch (error) {
            traceLogger.logTrace(0x1e11d3d3 /* tag_4e3pt */ , TraceLevel.Error, `Failed to save iCalendar banner dismissal state to localStorage: ${error}`);
        }
    };
    // Check iCalendar publishing status - follow same pattern as ICalendarDialogContainer
    React.useEffect(()=>{
        let enabled = false;
        if (planId) {
            // Check plan details for iCalendar publishing status
            const planDetails = planStore?.getPlanDetails(planId);
            if (planDetails) {
                enabled = planDetails.iCalendarPublishEnabled;
            }
        } else {
            // Check user settings for iCalendar publishing status
            const userSettings = userStore?.getUserSettings().data;
            if (userSettings) {
                enabled = userSettings.iCalendarPublishEnabled;
            }
        }
        setIsICalendarEnabled(enabled);
    }, [
        planId,
        planStore,
        userStore
    ]);
    // Don't render anything if iCalendar is not enabled or user has dismissed banner
    if (!isICalendarEnabled || hasSeenBanner) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ICalendarDeprecationBanner, {
        onDismiss: handleDismiss
    });
};
